// @ts-nocheck
import React, {
  useCallback,
  useRef,
  useMemo,
  useEffect,
  useState,
} from 'react';
import { Box, Typography, Chip, useTheme, alpha, Button } from '@mui/material';
import MonacoEditor, { loader } from '@monaco-editor/react';
import { ReactComponent as DragHandleIcon } from '../../../../../assets/Icons/lucideIcons/equal.svg';
import { ReactComponent as LockOutlinedIcon } from '../../../../../assets/Icons/lucideIcons/lock.svg';
import { ReactComponent as DataObjectIcon } from '../../../../../assets/Icons/lucideIcons/braces.svg';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';

import {
  useTableDispatch,
  useTableState,
} from '../../../context/TableSchemaBuilderContext';
import { registerCustomPython } from '../../../../Monaco/monacoCustomPython';
import { registerFormulaAttributeHoverProvider } from '../../../../Monaco/monacoHoverProviders';
import { validateFormulaAttributes } from '../../../../Monaco/monacoValidation';
import { insertTextAtCursor } from '../../../../../utils/monacoUtils';
import SwalToast from '../../../../Common/swalToast';
import SmartValuesModal from '../../../../Configuration/DerivedFormulaBuilder/Modals/SmartValuesModal';
import useTemplate from '../../../../../features/configuration/template/hooks/useTemplate';
import { EMPTY_ARRAY } from '../../../../../const/CommonConst';

loader.config({ paths: { vs: '/monaco' } });

const FormulaEditorPanel = ({ attributeList = EMPTY_ARRAY }) => {
  const theme = useTheme();
  const { schema, ui, config } = useTableState();
  const dispatch = useTableDispatch();
  const { customEntityList, entityList, getCustomEntityTemplate } =
    useTemplate();
  const cellData = schema?.cells?.[ui?.currentCell] || {};
  const isReadOnly = config?.readOnly || config?.editMode || false;

  // Internal state for editor content (before saving)
  const [editorValue, setEditorValue] = useState(cellData?.formula || '');
  const [isSmartValueModalOpen, setIsSmartValueModalOpen] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Refs to store Monaco model and editor instances
  const monacoModelRef = useRef(null);
  const monacoEditorRef = useRef(null);
  const hoverProviderRef = useRef(null);

  const isSmartValueButtonDisabled = useMemo(() => {
    return attributeList?.length === 0;
  }, [attributeList]);

  const smartValueButtonTitle = useMemo(() => {
    if (attributeList?.length === 0) {
      return 'No attributes available to use smart values.';
    }
    return 'Smart values';
  }, [attributeList]);

  // Update internal state when cell changes or modal opens
  useEffect(() => {
    setEditorValue(cellData?.formula || '');
  }, [cellData?.formula, ui?.currentCell]);

  // Debounced validation function to avoid excessive validation calls
  const debouncedValidation = useMemo(
    () =>
      debounce(() => {
        if (window.monaco && monacoModelRef.current) {
          validateFormulaAttributes(
            window.monaco,
            monacoModelRef.current,
            attributeList
          );
        }
      }, 300),
    [attributeList]
  );

  // Function to check for Monaco editor errors
  const checkForMonacoErrors = useCallback(() => {
    if (window.monaco && monacoModelRef.current) {
      // First, run validation to ensure markers are up to date
      validateFormulaAttributes(
        window.monaco,
        monacoModelRef.current,
        attributeList
      );

      // Get all markers (errors) for the current model
      const markers = window.monaco.editor.getModelMarkers({
        resource: monacoModelRef.current.uri,
      });

      // Filter for error markers (severity 8 is Error in Monaco)
      const errorMarkers = markers.filter(
        (marker) => marker.severity === window.monaco.MarkerSeverity.Error
      );

      if (errorMarkers.length > 0) {
        // Show toast message if there are validation errors
        SwalToast({
          icon: 'error',
          title:
            'Formula contains syntax errors. Please fix them before proceeding.',
        });
        return true; // Has errors
      }
    }
    return false; // No errors
  }, [attributeList]);

  const handleEditorChange = useCallback(
    (value) => {
      // Only update internal state, don't dispatch to main state yet
      if (!isReadOnly) {
        setEditorValue(value);
        // Trigger debounced validation when user types
        debouncedValidation();
      }
    },
    [isReadOnly, debouncedValidation]
  );

  const handleCancelEditor = useCallback(() => {
    // Reset to original value and close without saving
    setEditorValue(cellData?.formula || '');
    dispatch({ type: 'SET_SHOW_FORMULA_EDITOR', payload: false });
  }, [dispatch, cellData?.formula]);

  const handleOpenSmartValueModal = useCallback(() => {
    setIsSmartValueModalOpen(true);
  }, [setIsSmartValueModalOpen]);

  const handleCloseSmartValueModal = useCallback(() => {
    setIsSmartValueModalOpen(false);
  }, [setIsSmartValueModalOpen]);

  const handleSmartValueChange = useCallback(
    (newValue) => {
      if (monacoEditorRef?.current) {
        insertTextAtCursor(monacoEditorRef.current, newValue);
        setEditorValue(monacoEditorRef.current.getValue());
      } else {
        setEditorValue(newValue);
      }
    },
    [setEditorValue]
  );

  const handleCloseEditor = useCallback(() => {
    // Check for errors before closing
    const hasErrors = checkForMonacoErrors();
    if (!hasErrors) {
      // Save the formula to main state only if no errors
      dispatch({
        type: 'UPDATE_CELL_FORMULA',
        payload: {
          cellId: ui?.currentCell,
          formula: editorValue,
        },
      });
      // Close the editor
      dispatch({ type: 'SET_SHOW_FORMULA_EDITOR', payload: false });
    }
    // If there are errors, don't close the editor and don't save
  }, [dispatch, checkForMonacoErrors, ui?.currentCell, editorValue]);

  // Memoized handler for MonacoEditor onMount
  const handleEditorMount = useCallback(
    (editor, monaco) => {
      monacoModelRef.current = editor.getModel();
      monacoEditorRef.current = editor;
      // Initial validation
      validateFormulaAttributes(monaco, monacoModelRef.current, attributeList);
    },
    [attributeList]
  );

  // Memoized handler for MonacoEditor beforeMount
  const handleEditorBeforeMount = useCallback(
    (monaco) => {
      const derivedFormulaTheme =
        theme?.palette?.monacoEditor?.derivedFormulaEditor;
      // Dispose previous hover provider to prevent duplication
      if (hoverProviderRef.current) {
        hoverProviderRef.current.dispose();
        hoverProviderRef.current = null;
      }
      registerCustomPython(monaco, {
        tokenColors: derivedFormulaTheme?.tokenColors,
        colors: derivedFormulaTheme?.colors,
      });
      monaco?.editor?.setTheme('customPythonTheme');
      // Store the disposable in the ref so we can clean up next time
      hoverProviderRef.current = registerFormulaAttributeHoverProvider(
        monaco,
        attributeList
      );
    },
    [theme, attributeList]
  );

  // Clean up hover provider and debounced function on unmount
  useEffect(() => {
    return () => {
      if (hoverProviderRef.current) {
        hoverProviderRef.current.dispose();
        hoverProviderRef.current = null;
      }
      // Cancel any pending debounced validation calls
      debouncedValidation.cancel();
    };
  }, [debouncedValidation]);

  useEffect(() => {
    if (open) {
      setLoadingTemplates(true);
      customEntityList({}, false).finally(() => {
        setLoadingTemplates(false);
      });
    }
  }, [open]);

  const startResizing = useCallback(
    (e) => {
      const startX = e.pageX;
      const startWidth = ui?.editorWidth;

      const onMouseMove = (event) => {
        const newWidth = Math.max(300, startWidth - (event.pageX - startX));
        dispatch({
          type: 'SET_EDITOR_WIDTH',
          payload: newWidth,
        });
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [ui?.editorWidth, dispatch]
  );

  return (
    ui?.showFormulaEditor && (
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: `${ui?.editorWidth}px`,
          borderLeft: `1px solid ${alpha(theme?.palette?.primary?.main, 0.1)}`,
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1200,
          backgroundColor: theme?.palette?.other?.editorBackground,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1,
            borderBottom: `1px solid ${alpha(theme?.palette?.primary?.main, 0.1)}`,
            bgcolor: theme?.palette?.other?.editorBackground,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: theme?.palette?.other?.editorHeaderText,
              fontSize: '0.875rem',
            }}
          >
            <span style={{ color: theme?.palette?.primary?.main }}>
              Formula Editor
            </span>
            <span style={{ color: theme?.palette?.other?.toolbarText }}>
              {ui?.currentCell}
            </span>
            {isReadOnly && (
              <Chip
                icon={<LockOutlinedIcon width={18} height={18} />}
                label="Read Only"
                size="small"
                color="primary"
                sx={{ ml: 1, height: '16px' }}
              />
            )}
          </Typography>
          {/* Save Button Section */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              title="Close"
              onClick={handleCancelEditor}
            >
              Close
            </Button>
            {!isReadOnly && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  title="Save and Close"
                  onClick={handleCloseEditor}
                >
                  Save and Close
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleOpenSmartValueModal}
                  startIcon={<DataObjectIcon />}
                  disabled={isSmartValueButtonDisabled}
                  title={smartValueButtonTitle}
                  sx={{
                    '.MuiButton-icon': { margin: 0 },
                  }}
                >
                  {''}
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* Editor */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          <MonacoEditor
            height="100%"
            language="custom-python"
            theme="customPythonTheme"
            value={editorValue}
            onChange={handleEditorChange}
            beforeMount={handleEditorBeforeMount}
            onMount={handleEditorMount}
            options={{
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              fontSize: 14,
              lineNumbers: 'on',
              renderLineHighlight: 'all',
              scrollbar: {
                vertical: 'visible',
                horizontal: 'visible',
                verticalScrollbarSize: 10,
                horizontalScrollbarSize: 10,
              },
              folding: true,
              automaticLayout: true,
              tabSize: 2,
              wordWrap: 'on',
              suggest: {
                showMethods: true,
                showFunctions: true,
                showVariables: true,
              },
              // Set readOnly option based on the config
              readOnly: isReadOnly,
              // Adjust editor appearance in read-only mode
              ...(isReadOnly && {
                domReadOnly: true,
                cursorStyle: 'line-thin',
                cursorBlinking: 'solid',
              }),
              fixedOverflowWidgets: true, // The hover pop up should be on every element.
            }}
          />
        </Box>

        {/* Resize Handle */}
        <Box
          sx={{
            position: 'absolute',
            left: -8,
            top: 0,
            bottom: 0,
            width: 16,
            cursor: 'ew-resize',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme?.palette?.other?.editorResizeHandle,
            '&:hover': {
              color: theme?.palette?.other?.toolbarText,
            },
          }}
          onMouseDown={startResizing}
        >
          <DragHandleIcon
            width={18}
            height={18}
            style={{ transform: 'rotate(90deg)' }}
          />
        </Box>

        {/* Smart Values Modal */}
        <SmartValuesModal
          open={isSmartValueModalOpen}
          handleClose={handleCloseSmartValueModal}
          handleOnChange={handleSmartValueChange}
          value={editorValue || ''}
          attributes={attributeList}
          isConditionalModal={false}
          entityList={entityList}
          getCustomEntityTemplate={getCustomEntityTemplate}
          loadingTemplates={loadingTemplates}
          insertAtCursor={true}
        />
      </Box>
    )
  );
};

FormulaEditorPanel.propTypes = {
  attributeList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      data_type: PropTypes.string,
      description: PropTypes.string,
      is_derived: PropTypes.bool,
      input_control_type: PropTypes.string,
      derived_formula: PropTypes.string,
      attribute_origin: PropTypes.string,
    })
  ),
};

export default FormulaEditorPanel;
